import React, { useState, useEffect, useRef } from "react";
import { ArrowRight } from "lucide-react";
import DriveResults from "@assets/LandingPage/SuccessStories.png";

export default function ImpactMetrics() {
  const metrics = [
    { value: "500+", label: "Enterprise\nClients" },
    { value: "2.5M+", label: "Professionals\nTrained" },
    { value: "89%", label: "Training\nCompletion Rate" },
    { value: "40%", label: "Average\nProductivity Gain" },
    { value: "99.9%", label: "Platform\nUptime" },
    { value: "150+", label: "Countries\nServed" },
  ];

  const [displayValues, setDisplayValues] = useState(metrics.map(() => "0"));
  const [hasAnimated, setHasAnimated] = useState(false);
  const metricsRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            animateMetrics();
          }
        });
      },
      { threshold: 0.2 }
    );

    if (metricsRef.current) {
      observer.observe(metricsRef.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated]);

  const animateMetrics = () => {
    const duration = 2000; // Total animation duration - 2 seconds
    const shuffleCount = 20; // Number of shuffle iterations
    const shuffleInterval = duration / shuffleCount;

    metrics.forEach((metric, index) => {
      let iteration = 0;
      
      const interval = setInterval(() => {
        iteration++;
        
        if (iteration < shuffleCount) {
          // Generate random value during shuffle
          const randomValue = generateRandomValue(metric.value, iteration, shuffleCount);
          setDisplayValues(prev => {
            const newValues = [...prev];
            newValues[index] = randomValue;
            return newValues;
          });
        } else {
          // Set final value
          setDisplayValues(prev => {
            const newValues = [...prev];
            newValues[index] = metric.value;
            return newValues;
          });
          clearInterval(interval);
        }
      }, shuffleInterval);
    });
  };

  const generateRandomValue = (targetValue: string, iteration: number, totalIterations: number) => {
    // Extract number and suffix from target value
    const hasPlus = targetValue.includes('+');
    const hasPercent = targetValue.includes('%');
    const hasM = targetValue.includes('M');
    
    let targetNum = parseFloat(targetValue.replace(/[+%M]/g, ''));
    
    // Calculate progress (0 to 1)
    const progress = iteration / totalIterations;
    
    // Generate number that gradually approaches target
    let current;
    if (hasM) {
      current = (targetNum * progress).toFixed(1);
    } else if (hasPercent) {
      current = Math.floor(targetNum * progress);
    } else {
      current = Math.floor(targetNum * progress);
    }
    
    // Reconstruct with same format
    let result = current.toString();
    if (hasM) result += 'M';
    if (hasPlus) result += '+';
    if (hasPercent) result += '%';
    
    return result;
  };

  return (
    <>
      <style>{`
        .landing-drive-results-container {
          min-height: 100vh;
          background-color: var(--color-white);
          padding: 60px 16px;
          margin-top: 2rem;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
          [data-theme="dark"] .landing-drive-results-container {
            background: var(--color-white-dark);
          }

        .landing-drive-results-wrapper {
          max-width: 1280px;
          margin: 0 auto;
          width: 100%;
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          overflow: hidden;
        }

        .landing-drive-results-header {
          text-align: center;
          margin-bottom: 1rem;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.5rem;
          flex-shrink: 0;
        }

        .landing-drive-results-badge {
          display: inline-block;
          background-color: var(--color-golden-hour);
          color: var(--color-primary);
          padding: 10px 20px;
          border-radius: 3rem;
          font-weight: 500;
          margin-bottom: 16px;
          font-size: 14px;
        }

        .landing-drive-results-title {
          font-size: 32px;
          font-weight: 700;
          color: var(--color-primary);
          margin-bottom: 12px;
          line-height: 1.2;
        }
           [data-theme="dark"]  .landing-drive-results-title {
            color: var(--color-white);
          }

        @media (min-width: 768px) {
          .landing-drive-results-title {
            font-size: 3rem;
          }
        }

        .landing-drive-results-subtitle {
          color: var(--color-light-black);
          font-size: 1.2rem;
          max-width: 672px;
          margin: 0 auto;
          line-height: 1.5;
        }
           [data-theme="dark"]  .landing-drive-results-subtitle  {
            color: var(--color-white);
          }

        .landing-drive-results-metrics {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
          margin: 2.5rem 0;
          padding: 1rem 0 1.5rem 0 ;
          flex-shrink: 0;
        }

        @media (min-width: 768px) {
          .landing-drive-results-metrics {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (min-width: 1024px) {
          .landing-drive-results-metrics {
            grid-template-columns: repeat(6, 1fr);
          }
        }

        .landing-drive-results-metric-card {
          text-align: center;
        }

        .landing-drive-results-metric-value {
          font-size: 32px;
          font-weight: 700;
          color: var(--color-sunflare);
          margin-bottom: 6px;
        }

        @media (min-width: 1024px) {
          .landing-drive-results-metric-value {
            font-size: 36px;
          }
        }

        .landing-drive-results-metric-label {
          color: var(--color-slate-gray);
          font-size: 1rem;
          white-space: pre-line;
        }
           [data-theme="dark"] .landing-drive-results-metric-label  {
            color: var(--color-grey);
          }

        .landing-drive-results-bottom {
          display: grid;
          grid-template-columns: 1fr;
          gap: 24px;
          align-items: center;
          flex: 1;
          min-height: 0;
        }

        @media (min-width: 768px) {
          .landing-drive-results-bottom {
            grid-template-columns: 1fr 1fr;
            gap: 48px;
          }
        }

        .landing-drive-results-image-container {
          order: 2;
             display:flex;
          justify-content:center;
            align-items:center;

        }

        @media (min-width: 768px) {
          .landing-drive-results-image-container {
            order: 1;
            
          }
        }

        .landing-drive-results-image {
          width: 90%;
          height: auto;
          max-height: 250px;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          object-fit: cover;
        }

        @media (min-width: 768px) {
          .landing-drive-results-image {
            max-height: 280px;
          }
        }

        @media (min-width: 1024px) {
          .landing-drive-results-image {
            max-height: 320px;
          }
        }

        .landing-drive-results-content {
          padding: 0 4rem;
          order: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
        }

            @media (min-width: 990px) {
          .landing-drive-results-content {
             padding: 0 2rem;
          }
        }

        @media (min-width: 768px) {
          .landing-drive-results-content {
              order: 1;
              padding: 0 1rem;
          }
        }
             @media (min-width: 480px) {
          .landing-drive-results-content {
              padding: 0rem;
          }
        }

        .landing-drive-results-content-title {
          font-size: 30px;
          font-weight: 700;
          color: var(--color-black);
          margin-bottom: 12px;
          line-height: 1.2;
        }
           [data-theme="dark"] .landing-drive-results-content-title {
            color: var(--color-white);
          }

        @media (min-width: 768px) {
          .landing-drive-results-content-title {
            font-size: 28px;
          }
        }

        .landing-drive-results-content-text {
          color: var(--color-slate-gray);
          font-size: 18px;
          margin-bottom: 16px;
          line-height: 1.6;
        }
            [data-theme="dark"] .landing-drive-results-content-text {
            color: var(--color-grey);
          }

        /* Secondary Button - Matches Hero Section */
        .landing-drive-results-button {
          display: inline-flex;
          align-items: center;
          gap: var(--spacing-sm, 8px);
          padding: 1.1rem 2rem;
          background: transparent;
          color: var(--color-primary);
          border: 1px solid var(--color-sunflare);
          border-radius: var(--radius-full, 50px);
          font-size: 1rem;
          font-weight: var(--font-weight-semibold, 600);
          cursor: pointer;
          position: relative;
          overflow: visible;
          transform-origin: left center;
          transition:
            padding-right 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55),
            transform 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
          width: fit-content;
          align-self: flex-start;
        }
        [data-theme="dark"] .landing-drive-results-button {
          color: var(--color-white);
        }

        .landing-drive-results-button span {
          display: inline-block;
        }

        .landing-drive-results-button-icon {
          width: 20px;
          height: 20px;
          display: inline-block;
          transition: transform 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }

        .landing-drive-results-button:hover .landing-drive-results-button-icon {
          transform: translateX(15px);
        }

        .landing-drive-results-button:hover {
          padding-right: 2.5rem;
          transform: scaleX(1.01);
        }
      `}</style>

      <div className="landing-drive-results-container">
        <div className="landing-drive-results-wrapper">
          {/* Header Section */}
          <div className="landing-drive-results-header">
            <div className="landing-drive-results-badge">Impact Metrics</div>
            <h1 className="landing-drive-results-title">
              Numbers That Drive Results
            </h1>
            <p className="landing-drive-results-subtitle">
              Real metrics from global enterprises who've transformed their
              workforce with our learning platform.
            </p>
          </div>

          {/* Metrics Grid */}
          <div className="landing-drive-results-metrics" ref={metricsRef}>
            {metrics.map((metric, index) => (
              <div key={index} className="landing-drive-results-metric-card">
                <div className="landing-drive-results-metric-value">
                  {displayValues[index]}
                </div>
                <div className="landing-drive-results-metric-label">
                  {metric.label}
                </div>
              </div>
            ))}
          </div>

          {/* Bottom Section */}
          <div className="landing-drive-results-bottom">
            {/* Image - LEFT SIDE */}
            <div className="landing-drive-results-image-container">
              <img
                src={DriveResults}
                alt="Team achieving success and growth"
                className="landing-drive-results-image"
              />
            </div>

            {/* Content - RIGHT SIDE */}
            <div className="landing-drive-results-content">
              <h2 className="landing-drive-results-content-title">
                Ready to Join These Success Stories?
              </h2>
              <p className="landing-drive-results-content-text">
                Discover how organizations like yours are transforming learning
                experiences and driving measurable growth with MILE LXP. See how
                our platform empowers teams to learn smarter, perform better,
                and achieve lasting success.
              </p>
              <button className="landing-drive-results-button">
                <span>Watch Demo for free</span>
                <ArrowRight className="landing-drive-results-button-icon" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}